import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Camera, Video as VideoIcon,
  Music, Disc3, PartyPopper, Wine, Globe, Mic2, Sparkles, X, Calendar as CalendarIcon, Clock,
} from 'lucide-react';
import { useVenue } from '../../contexts/VenueContext';
import { API, getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { LoadingVideo } from '../../components/LoadingVideo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const font = "'Inria Serif', serif";

const cardStyle = {
  backgroundColor: 'var(--app-bg)',
  borderRadius: 20,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

const CATEGORIES = [
  { key: 'karaoke', label: 'Karaoke', Icon: Mic2 },
  { key: 'live-music', label: 'Live Music', Icon: Music },
  { key: 'dj-set', label: 'DJ Set', Icon: Disc3 },
  { key: 'party', label: 'Party', Icon: PartyPopper },
  { key: 'greek-night', label: 'Greek Night', Icon: Sparkles },
  { key: 'wine-tasting', label: 'Wine Tasting', Icon: Wine },
  { key: 'cultural-event', label: 'Cultural Event', Icon: Globe },
  { key: 'other', label: 'Other', Icon: Sparkles },
];

const MAX_VIDEO_DURATION_SECONDS = 15;
const MAX_MINI_DESCRIPTION = 60;
const MAX_CUSTOM_CATEGORY = 18;

function capitalizeFirst(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseISODateLocal(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function shortDay(iso) {
  const d = parseISODateLocal(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short' });
}

function dayOfMonth(iso) {
  return parseISODateLocal(iso).getDate();
}

function dayMonthLabel(iso) {
  return parseISODateLocal(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function timeOptions() {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
}

const TIME_OPTIONS = timeOptions();

function WindowCalendar({ todayISO, maxISO, blockedDays, selectedStart, selectedEnd, onSelect, editingId }) {
  // Build a flat 7-day list starting from today.
  const days = [];
  if (todayISO) {
    const start = parseISODateLocal(todayISO);
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(toISO(d));
    }
  }

  const blockedSet = new Set(blockedDays || []);
  const editingBlockedSet = new Set();
  // If editing, the event's own current days are still selectable.
  // The backend already excludes them via ignore_event_id when blocked-days
  // is fetched, but we double-defend here.

  const inRange = (iso) => {
    if (!selectedStart || !selectedEnd) return false;
    return iso >= selectedStart && iso <= selectedEnd;
  };

  return (
    <div data-testid="event-calendar" style={{
      ...cardStyle, padding: 16,
      display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6,
    }}>
      {days.map((iso) => {
        const isBlocked = blockedSet.has(iso) && !editingBlockedSet.has(iso);
        const isStart = iso === selectedStart;
        const isEnd = iso === selectedEnd;
        const inSel = inRange(iso);
        const past = todayISO && iso < todayISO;
        const beyondWindow = maxISO && iso > maxISO;
        const disabled = isBlocked || past || beyondWindow;
        return (
          <button
            key={iso}
            type="button"
            data-testid={`event-day-${iso}`}
            disabled={disabled}
            onClick={() => onSelect?.(iso)}
            style={{
              border: 'none',
              padding: '8px 0',
              cursor: disabled ? 'not-allowed' : 'pointer',
              borderRadius: 12,
              background: isStart || isEnd ? 'var(--accent-purple)' : inSel ? 'var(--accent-surface-soft)' : disabled ? 'var(--surface-2)' : 'var(--surface)',
              color: isStart || isEnd ? '#FFFFFF' : disabled ? 'var(--text-3)' : 'var(--text)',
              fontFamily: font, fontWeight: 700,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              boxShadow: (isStart || isEnd) ? 'var(--premium-shadow-main)' : 'none',
              transition: 'background 160ms ease, color 160ms ease',
              position: 'relative',
              textDecoration: disabled && !past ? 'line-through' : 'none',
            }}
          >
            <span style={{ fontSize: 10, opacity: 0.85 }}>{shortDay(iso)}</span>
            <span style={{ fontSize: 15 }}>{dayOfMonth(iso)}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function VenueEventCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Same component is mounted under both /venue and /staff-admin.
  const basePath = location.pathname.startsWith('/staff-admin') ? '/staff-admin' : '/venue';
  const { eventId } = useParams();
  const { activeVenueAppId } = useVenue();
  const isEdit = Boolean(eventId);

  const [category, setCategory] = useState('karaoke');
  const [customCategory, setCustomCategory] = useState('');
  const [miniDescription, setMiniDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('20:00');
  const [endTime, setEndTime] = useState('23:00');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [todayLocal, setTodayLocal] = useState(null);
  const [maxStartDate, setMaxStartDate] = useState(null);
  const [blockedDays, setBlockedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchWindow = useCallback(async () => {
    try {
      const params = { ...getVenueParams(activeVenueAppId) };
      if (eventId) params.ignore_event_id = eventId;
      const res = await axios.get(`${API}/venue/events/blocked-days`, {
        headers: getAuthHeaders(),
        params,
      });
      setBlockedDays(res.data?.blocked_days || []);
      setTodayLocal(res.data?.today_local || null);
      setMaxStartDate(res.data?.max_start_date || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load calendar window');
    }
  }, [activeVenueAppId, eventId]);

  const fetchExisting = useCallback(async () => {
    if (!isEdit) return;
    try {
      const res = await axios.get(`${API}/venue/events`, {
        headers: getAuthHeaders(),
        params: { ...getVenueParams(activeVenueAppId), scope: 'all' },
      });
      const found = (res.data?.events || []).find((e) => e.id === eventId);
      if (!found) {
        toast.error('Event not found');
        navigate(`${basePath}/events`);
        return;
      }
      setCategory(found.category || 'karaoke');
      setCustomCategory(found.custom_category || '');
      setMiniDescription(found.mini_description || '');
      setStartDate(found.start_date || '');
      setEndDate(found.end_date || '');
      setStartTime(found.start_time || '20:00');
      setEndTime(found.end_time || '23:00');
      setMediaUrl(found.media_url || '');
      setMediaType(found.media_type || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load event');
    }
  }, [isEdit, activeVenueAppId, eventId, navigate]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await Promise.all([fetchWindow(), fetchExisting()]);
      setLoading(false);
    };
    if (activeVenueAppId) run();
  }, [activeVenueAppId, fetchWindow, fetchExisting]);

  const handleSelectDay = (iso) => {
    // Single-day events only — clicking always sets both start and end to
    // the same date. Overnight events (end_time < start_time) get end_date
    // auto-bumped to the next day in handleSubmit.
    setStartDate(iso);
    setEndDate(iso);
  };

  const handleMediaPick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isVideo = (file.type || '').startsWith('video/');
    if (!file.type.startsWith('image/') && !isVideo) {
      toast.error('Pick a photo or short video');
      return;
    }
    if (isVideo) {
      // Enforce 15-second limit client-side.
      const ok = await new Promise((resolve) => {
        try {
          const url = URL.createObjectURL(file);
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const duration = Number.isFinite(video.duration) ? video.duration : 0;
            if (duration > MAX_VIDEO_DURATION_SECONDS + 1) {
              toast.error(`Videos must be ≤ ${MAX_VIDEO_DURATION_SECONDS} seconds`);
              resolve(false);
            } else {
              resolve(true);
            }
          };
          video.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(true); // tolerate metadata errors; backend size cap still applies
          };
          video.src = url;
        } catch {
          resolve(true);
        }
      });
      if (!ok) return;
    }
    setUploading(true);
    // Upload stays snappy: heavy ffmpeg optimize now runs in a backend
    // BackgroundTask after the response returns, so this toast only
    // covers the raw byte transfer.
    const toastId = toast.loading(isVideo ? 'Uploading your video…' : 'Uploading photo…');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/venue/events/upload-media`, formData, {
        headers: { ...getAuthHeaders() },
      });
      setMediaUrl(res.data?.url || '');
      setMediaType(res.data?.media_type || (isVideo ? 'video' : 'photo'));
      toast.success('Media uploaded', { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!category) {
      toast.error('Pick an event type');
      return;
    }
    if (category === 'other' && !customCategory.trim()) {
      toast.error('Add a 2-word name for your event');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Pick the event dates');
      return;
    }
    if (!startTime || !endTime) {
      toast.error('Pick start and end times');
      return;
    }
    // For overnight events (end_time < start_time) on a single-day selection,
    // auto-bump end_date by 1 day so the backend's strict "end > start" check
    // doesn't reject what the user clearly meant.
    let effectiveEndDate = endDate;
    if (startDate === endDate && endTime <= startTime) {
      const next = new Date(parseISODateLocal(endDate));
      next.setDate(next.getDate() + 1);
      const nextIso = toISO(next);
      // Make sure the bumped day is still within the 7-day window and not
      // blocked by another event.
      if (maxStartDate && nextIso > toISO(new Date(parseISODateLocal(maxStartDate).getTime() + 2 * 86400000))) {
        toast.error('Overnight end falls outside the booking window');
        return;
      }
      if (blockedDays.includes(nextIso)) {
        toast.error('Another event blocks the day this overnight event ends on');
        return;
      }
      effectiveEndDate = nextIso;
    }
    const payload = {
      venue_app_id: activeVenueAppId,
      category,
      custom_category: category === 'other' ? customCategory.trim() : '',
      mini_description: miniDescription.trim(),
      start_date: startDate,
      end_date: effectiveEndDate,
      start_time: startTime,
      end_time: endTime,
      media_url: mediaUrl || null,
      media_type: mediaUrl ? mediaType : null,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/venue/events/${eventId}`, payload, { headers: getAuthHeaders() });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/venue/events`, payload, { headers: getAuthHeaders() });
        toast.success('Event published');
      }
      navigate(`${basePath}/events`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save event');
    } finally {
      setSubmitting(false);
    }
  };

  const mediaPreviewSrc = useMemo(() => {
    if (!mediaUrl) return null;
    if (mediaUrl.startsWith('/')) return `${BACKEND_URL}${mediaUrl}`;
    return mediaUrl;
  }, [mediaUrl]);

  return (
    <div className="app-shell" data-testid="venue-event-create-page" style={{
      minHeight: '100vh',
      background: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingBottom: 120,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
        <button
          onClick={() => navigate(`${basePath}/events`)}
          data-testid="venue-event-create-back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
        <div style={{ width: 30 }} />
      </div>

      <div style={{ marginTop: 6 }}>
        <RouteTransitionLogo width={92} height={82} dataTestId="venue-event-create-shared-logo" />
      </div>

      <h2 style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 18 }}>
        {isEdit ? 'Edit event' : 'New event'}
      </h2>

      {loading ? (
        <p style={{ fontFamily: font, color: 'var(--text-3)' }}>Loading…</p>
      ) : (
        <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, display: 'grid', gap: 18 }}>
          {/* Category grid */}
          <section>
            <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', margin: '0 0 8px' }}>Event type</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {CATEGORIES.map(({ key, label, Icon }) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`event-category-${key}`}
                    onClick={() => setCategory(key)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '10px 6px', borderRadius: 14, cursor: 'pointer',
                      border: active ? '1.5px solid var(--accent-purple)' : '1px solid var(--card-border)',
                      background: active ? 'var(--accent-surface)' : 'var(--surface)',
                      color: active ? 'var(--accent-purple)' : 'var(--text)',
                      fontFamily: font, fontSize: 10.5, fontWeight: 700,
                      lineHeight: 1.15, textAlign: 'center',
                      boxShadow: active ? 'var(--premium-shadow-nested)' : 'none',
                    }}
                  >
                    <Icon size={16} strokeWidth={2.2} />
                    {label}
                  </button>
                );
              })}
            </div>
            {category === 'other' ? (
              <input
                data-testid="event-custom-category-input"
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(capitalizeFirst(e.target.value.slice(0, MAX_CUSTOM_CATEGORY)))}
                placeholder="e.g. Jazz Lounge"
                maxLength={MAX_CUSTOM_CATEGORY}
                style={{
                  marginTop: 10, width: '100%',
                  padding: '12px 14px', borderRadius: 12,
                  border: '1px solid var(--card-border)',
                  fontFamily: font, fontSize: 14, color: 'var(--text)',
                  outline: 'none', background: 'var(--surface)',
                }}
              />
            ) : null}
          </section>

          {/* Mini description */}
          <section>
            <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', margin: '0 0 8px' }}>
              Mini description <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>(optional · {miniDescription.length}/{MAX_MINI_DESCRIPTION})</span>
            </p>
            <textarea
              data-testid="event-mini-description-input"
              value={miniDescription}
              onChange={(e) => setMiniDescription(capitalizeFirst(e.target.value.slice(0, MAX_MINI_DESCRIPTION)))}
              placeholder="Tonight we sing together!"
              rows={2}
              style={{
                width: '100%', resize: 'none',
                padding: '12px 14px', borderRadius: 12,
                border: '1px solid var(--card-border)',
                fontFamily: font, fontSize: 14, color: 'var(--text)',
                outline: 'none', background: 'var(--surface)',
              }}
            />
          </section>

          {/* Calendar */}
          <section>
            <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarIcon size={13} strokeWidth={2.4} />
              When · <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>
                {startDate
                  ? dayMonthLabel(startDate)
                  : 'pick a day within the next week'}
              </span>
            </p>
            <WindowCalendar
              todayISO={todayLocal || toISO(new Date())}
              maxISO={maxStartDate}
              blockedDays={blockedDays}
              selectedStart={startDate}
              selectedEnd={endDate}
              onSelect={handleSelectDay}
              editingId={eventId}
            />
          </section>

          {/* Time pickers */}
          <section>
            <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={13} strokeWidth={2.4} /> Time
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { label: 'Start', value: startTime, set: setStartTime, tid: 'event-start-time' },
                { label: 'End', value: endTime, set: setEndTime, tid: 'event-end-time' },
              ].map((t) => (
                <label key={t.label} style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontFamily: font, fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 }}>{t.label}</span>
                  <select
                    data-testid={t.tid}
                    value={t.value}
                    onChange={(e) => t.set(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 12px',
                      borderRadius: 12, border: '1px solid var(--card-border)',
                      background: 'var(--surface)', color: 'var(--text)',
                      fontFamily: font, fontSize: 14, outline: 'none',
                    }}
                  >
                    {TIME_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </section>

          {/* Media uploader */}
          <section>
            <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', margin: '0 0 8px' }}>
              Event media <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>(optional · 1 photo OR 1 video ≤ 15s max)</span>
            </p>
            {mediaPreviewSrc ? (
              <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 20px rgba(20, 16, 50, 0.18)' }}>
                {mediaType === 'video' ? (
                  <LoadingVideo
                    dataTestId="event-media-preview-video"
                    src={mediaPreviewSrc}
                    wrapperStyle={{ width: '100%', height: 220 }}
                  />
                ) : (
                  <img
                    data-testid="event-media-preview-image"
                    src={mediaPreviewSrc}
                    alt="Event media"
                    style={{ width: '100%', display: 'block', maxHeight: 220, objectFit: 'cover' }}
                  />
                )}
                <button
                  type="button"
                  data-testid="event-media-remove"
                  onClick={() => { setMediaUrl(''); setMediaType(null); }}
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(18,18,28,0.6)', color: '#FFFFFF',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label="Remove media"
                >
                  <X size={14} strokeWidth={2.4} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleMediaPick}
                data-testid="event-media-picker"
                style={{
                  ...cardStyle, width: '100%',
                  padding: '18px 14px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  cursor: uploading ? 'wait' : 'pointer', border: '1px dashed var(--card-border)',
                  background: 'var(--surface)',
                  textAlign: 'left',
                }}
              >
                <div style={{ width: 42, height: 42, borderRadius: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)' }}>
                  <Camera size={18} strokeWidth={2.2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                    {uploading ? 'Uploading…' : 'Upload a photo or short video'}
                  </p>
                  <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 12, color: 'var(--text-3)' }}>
                    Helps your event stand out on the guest map.
                  </p>
                </div>
                <VideoIcon size={16} color="var(--text-3)" strokeWidth={2} />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleMediaUpload}
              style={{ display: 'none' }}
              data-testid="event-media-input"
            />
          </section>

          {/* Submit */}
          <button
            type="button"
            data-testid="venue-event-submit"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              marginTop: 8,
              width: '100%', padding: '14px 18px', borderRadius: 18, border: 'none',
              cursor: submitting ? 'wait' : 'pointer',
              background: submitting ? 'var(--accent-surface)' : 'var(--accent-purple)',
              color: '#FFFFFF', fontFamily: font, fontSize: 15.5, fontWeight: 700,
              boxShadow: '0 12px 24px rgba(232, 25, 44, 0.25)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Publish event'}
            {!submitting ? <ChevronRight size={16} strokeWidth={2.4} /> : null}
          </button>
        </div>
      )}
    </div>
  );
}
